import { Box } from "@mui/system";
import { DataGridPro, GridColumnVisibilityModel, GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { useMemo, useState, useCallback, useRef } from "react";
import { useDisableKoBiddingAutoMaxCpcMutation, useDisableKoBiddingManualMaxCpcMutation, useUpdateKoBiddingAutoMaxCpcMutation, useUpdateKoBiddingManualMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import ConfirmDisableModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-confirm-disable";
import { FilterPanelCustom } from "../filter-panel-custom";
import ModalDeactivateStrategy from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-deactivate-strategy";
import { useEnforcePortfolioMaxCPCMutation, useEnforceMaxCpcCampaignTargetImpressionShareMutation, useEnforceMaxCPCMutation } from "src/context/api/liveBidding/api";
import EnforceMaxCpcModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-enforce-max-cpc";
import ModalCreateStrategyCampaigns from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-create-strategy-campaigns";
import ModalActivationsAuto from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-activations-auto";
import { gridStyles } from "./styles/grid";
import { useAuth } from "src/auth";
import { useCampaignColumns } from "./utils/useCampaignColumns";
import CustomToolbar from "./components/CustomToolbar";
import { dateRangeOptions, comparisonPeriodOptions } from "./constants/dateOptions";
import { useModals } from "./hooks/useModals";
import { useActions } from "./hooks/data-grid-campaign/useActions";

interface Props {
    data: any[];
    rowCount: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    sortModel: GridSortModel;
    setSortModel: (model: GridSortModel) => void;
    filterModel: GridFilterModel;
    setFilterModel: (model: GridFilterModel) => void;
    localPatches: Record<string, any>;
    setLocalPatches: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    selectedDateRange?: { label: string; value: string };
    onDateRangeChange?: (newRange: { label: string; value: string }) => void;
    selectedCompareDate?: any;
    onCompareDateChange?: (newRange: { label: string; value: string }) => void;
    refetch: any,
}

export default function DataGridCampaigns({
    data,
    rowCount,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    localPatches,
    setLocalPatches,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    onDateRangeChange,
    onCompareDateChange,
    selectedCompareDate,
    selectedDateRange,
    refetch
}: Props) {
    const [selectionRows, setSelectionRows]: any = useState([])
    const workspace: any = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';
    const apiRef: any = useRef();
    const toolbarRef = useRef<HTMLDivElement>(null);
    const { user } = useAuth();

    const [disableKoBiddingManualMaxCpc] = useDisableKoBiddingManualMaxCpcMutation();
    const [disableKoBiddingAutoMaxCpc] = useDisableKoBiddingAutoMaxCpcMutation();
    const [enforceMaxCPCTargetImpressionShare] = useEnforceMaxCpcCampaignTargetImpressionShareMutation();
    const [enforcePortfolioStrategy] = useEnforcePortfolioMaxCPCMutation();

    const [updateMaxCpc] = useUpdateKoBiddingManualMutation();
    const [updateKOBiddingAutoMaxCpc] = useUpdateKoBiddingAutoMaxCpcMutation()

    const { modalActivationAuto, setModalActivationAuto, modalCreateStrategy, setModalCreateStrategy,
        modalDeactivateStrategy, setModalDeactivateStrategy, confirmDisable, setConfirmDisable,
        openEnforceModal, setOpenEnforceModal
    } = useModals();

    const handleCloseActivationAuto = useCallback(() => setModalActivationAuto({ open: false, row: null }), []);

    const columns = useCampaignColumns({
        workspace,
        lang,
        selectedCompareDate,
        setModalActivationAuto,
        setConfirmDisable,
        setModalCreateStrategy,
    });

    const [modifiedRows, setModifiedRows] = useState<any[]>([]);

    const { handleConfirmDisable, handleFinish, handleUpdateMaxCPC, handleRowUpdate } = useActions({
        workspace,
        confirmDisable,
        data,
        enforceMaxCPCTargetImpressionShare,
        updateMaxCpc,
        disableKoBiddingManualMaxCpc,
        enforcePortfolioStrategy,
        disableKoBiddingAutoMaxCpc,
        setLocalPatches,
        setModifiedRows,
        updateKOBiddingAutoMaxCpc,
        setConfirmDisable,
        refetch
    });

    const mergedRows = useMemo(() => {
        return data.map((row) => {
            const patch = localPatches[row.id];
            if (!patch) return row;

            const existing = row.activations_details || [];
            const patched = patch.activations_details || [];

            const mergedExisting = existing.map((a: any) => {
                const found = patched.find((p: any) => p.id === a.id);
                return found ? { ...a, ...found } : a;
            });

            const newOnes = patched.filter(
                (p: any) => !existing.some((a: any) => a.id === p.id)
            );

            return {
                ...row,
                activations_details: [...mergedExisting, ...newOnes],
            };
        });
    }, [data, localPatches]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => {
        const saved = localStorage.getItem("columnVisibilityModelActivationsCamapaigns");
        return saved ? JSON.parse(saved) : {
            campaign_id: false,
            ad_group_id: false,
            target_cpa: false,
            target_roas: false,
            target_impression_share: false,
            target_impression_share_position: false,
            platform: false
        };
    });

    const handleColumnVisibilityChange = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
        localStorage.setItem("columnVisibilityModelActivationsCamapaigns", JSON.stringify(newModel));
    }, []);

    return (
        <Box sx={gridStyles}>
            <DataGridPro
                rowHeight={57}
                processRowUpdate={handleRowUpdate}
                apiRef={apiRef}
                getRowId={(r) => r.id}
                columnHeaderHeight={50}
                rows={mergedRows}
                rowCount={rowCount}
                paginationModel={{ page, pageSize }}
                pageSizeOptions={[10, 25, 50, 100, 200]}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                onPaginationModelChange={({ page, pageSize }) => {
                    onPageChange(page);
                    onPageSizeChange(pageSize);
                }}
                pagination
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                keepNonExistentRowsSelected
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                slots={{
                    toolbar: () => <CustomToolbar
                        toolbarRef={toolbarRef}
                        apiRef={apiRef}
                        selectionRows={selectionRows}
                        canImport={["admin", "brand specialist"].includes(user?.role ?? "")}
                        selectedDateRange={selectedDateRange}
                        selectedCompareDate={selectedCompareDate}
                        onDateRangeChange={onDateRangeChange}
                        onCompareDateChange={onCompareDateChange}
                        modifiedRows={modifiedRows}
                        setModifiedRows={setModifiedRows}
                        setLocalPatches={setLocalPatches}
                        handleUpdateMaxCPC={handleUpdateMaxCPC}
                        setModalDeactivateStrategy={setModalDeactivateStrategy}
                        setModalCreateStrategy={setModalCreateStrategy}
                        setOpenEnforceModal={setOpenEnforceModal}
                        dateRangeOptions={dateRangeOptions}
                        comparisonPeriodOptions={comparisonPeriodOptions}
                    />,
                    filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
                }}
                checkboxSelection
                onRowSelectionModelChange={(newSelection: any) => {
                    const selectedIds = Array.isArray(newSelection) ? newSelection : [];
                    setSelectionRows(
                        mergedRows.filter((row) => selectedIds.includes(row.id))
                    );
                }}
                getRowClassName={(params) =>
                    [
                        params.row.isEdited ? "edited-row" : "",
                        params.row.isInvalid ? "row-invalid" : ""
                    ].join(" ")
                }
                isCellEditable={(params) => {
                    const activations =
                        params.row.activations_details || [];

                    const activation = activations.find(
                        (d: any) =>
                            d.status === true &&
                            (
                                d.mode ===
                                "KO Bidding Manual" ||
                                d.mode?.startsWith(
                                    "KO Bidding Auto"
                                )
                            )
                    );

                    if (!activation) return false;

                    const isAuto =
                        activation.mode?.startsWith(
                            "KO Bidding Auto"
                        );

                    const strategy = (
                        params?.row?.bidding_strategy_type || ""
                    ).trim();

                    if (isAuto) {

                        const autoFieldsMap: Record<
                            string,
                            string[]
                        > = {
                            "Target ROAS": [
                                "target_roas",
                            ],

                            "Maximize Conversion Value": [
                                "target_roas",
                            ],
                        };

                        const allowedFields =
                            autoFieldsMap[strategy] ?? [];

                        return allowedFields.includes(
                            params.field
                        );
                    }

                    const strategyFieldsMap: Record<
                        string,
                        string[]
                    > = {
                        "Target ROAS": [
                            "max_cpc",
                            "target_roas",
                        ],

                        "Maximize Conversion Value": [
                            "max_cpc",
                            "target_roas",
                        ],

                        "Target CPA": [
                            "max_cpc",
                            "target_cpa",
                        ],

                        "Maximize Conversions": [
                            "max_cpc",
                            "target_cpa",
                        ],

                        "Target Impression Share": [
                            "max_cpc",
                            "target_impression_share",
                            "target_impression_share_position",
                        ],
                    };

                    const allowedFields =
                        strategyFieldsMap[strategy] ??
                        ["max_cpc"];

                    return allowedFields.includes(
                        params.field
                    );
                }}
                disableRowSelectionOnClick
                columns={columns}
            />

            <ModalCreateStrategyCampaigns
                refetch={refetch}
                open={modalCreateStrategy.open}
                row={modalCreateStrategy.row}
                onClose={() => setModalCreateStrategy({ open: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            {modalActivationAuto.open &&
                <ModalActivationsAuto
                    open={modalActivationAuto.open}
                    row={modalActivationAuto.row}
                    onClose={handleCloseActivationAuto}
                    setLocalPatches={setLocalPatches}
                    tab={"campaign"}
                />
            }

            <ModalDeactivateStrategy
                refetch={refetch}
                open={modalDeactivateStrategy.open}
                row={modalDeactivateStrategy.row}
                onClose={() => setModalDeactivateStrategy({ open: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ConfirmDisableModal
                open={confirmDisable.open}
                row={confirmDisable.row}
                onClose={() => setConfirmDisable({ open: false, row: null, mode: null })}
                onConfirm={handleConfirmDisable}
            />

            <EnforceMaxCpcModal
                onFinish={handleFinish}
                open={openEnforceModal.open}
                onClose={() => setOpenEnforceModal({ open: false, row: null })}
                selectedRows={openEnforceModal.row}
            />
        </Box >
    );
}